import React from "react";
import { CloseIcon, FilterIcon } from "@components/Icons";
import {
  Stack,
  Drawer,
  Typography,
  IconButton,
  TextField,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { useFormik } from "formik";
import { useState, useEffect, Fragment, useRef } from "react";
import { initialValues, validationSchema } from "../models/schema";
import Button from "@components/Button";
import { FilterAltOutlined } from "@mui/icons-material";
import { useMyDriversContext } from "@context/MyDriversContext";
import { useAuth } from "@context/auth/AuthContext";
import DropdownSelectAll from "@components/DropdownSelectAll";

export const FilterButton = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [companies, setCompanies] = useState<string[]>([]);

  const ref = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { listCNPJ } = useAuth();

  const options = listCNPJ.map((item) => ({
    label: item.companyName,
    value: item.CNPJ,
  }));

  const {
    loading,
    filters,
    setPage,
    setFilters,
    fetchAllDrivers,
  } = useMyDriversContext();

  const { handleSubmit, handleBlur, setFieldValue, errors } = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async () => {
      setPage(0);
      await fetchAllDrivers();
      setOpenDrawer(false);
    },
  });

  const handleSetDriverQuery = (value: string) => {
    setFieldValue("driverQuery", value);
    setFilters((prevState) => ({
      ...prevState,
      driverQuery: value,
    }));
  };

  const handleMountList = (value: string[]) => {
    setCompanies(value);

    const listCompanyDocuments = value.join(",");

    setFilters((prevState) => ({
      ...prevState,
      companyDocuments: listCompanyDocuments,
    }));
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      ref?.current?.focus();
    }, 100);

    return () => clearTimeout(timer);
  }, [openDrawer]);

  return (
    <Fragment>
      {loading ? (
        <Skeleton animation="wave" height="5rem" width="10rem" />
      ) : (
        <Button
          fullWidth={isMobile}
          variant="outlined"
          startIcon={<FilterAltOutlined />}
          onClick={() => setOpenDrawer(true)}
          sx={{ minHeight: "44px" }}
        >
          Filtrar
        </Button>
      )}

      <Drawer
        open={openDrawer}
        onClose={() => handleCloseDrawer()}
        anchor={isMobile ? "bottom" : "right"}
        elevation={16}
        sx={{
          zIndex: 1201,
          "& .MuiDrawer-paper": {
            width: isMobile ? "100%" : "30rem",
            maxHeight: isMobile ? "90vh" : "100vh",
            borderRadius: isMobile ? "16px 16px 0 0" : 0,
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2.5, pt: 2.5, pb: 1, flexShrink: 0 }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem" }}>
            Filtrar dados
          </Typography>
          <IconButton
            onClick={() => setOpenDrawer(false)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        {/* Icone decorativo - só no desktop */}
        {!isMobile && (
          <Stack alignSelf="center" sx={{ py: 1 }}>
            <FilterIcon />
          </Stack>
        )}

        {/* Conteúdo dos filtros - scrollável */}
        <Stack
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            py: 2,
            gap: 2.5,
          }}
        >
          <Stack sx={{ width: "100%" }}>
            <Typography
              color="shadesOfDark.dark"
              sx={{ mb: 0.5, fontWeight: 700, fontSize: "1.3rem" }}
            >
              Nome ou e-mail
            </Typography>
            <TextField
              fullWidth
              size={isMobile ? "medium" : "small"}
              inputRef={ref}
              placeholder="Nome ou e-mail"
              value={filters.driverQuery}
              onChange={({ target: { value } }) => handleSetDriverQuery(value)}
              onBlur={handleBlur("driverQuery")}
              error={Boolean(errors.driverQuery)}
              helperText={errors.driverQuery}
            />
          </Stack>

          <Stack sx={{ width: "100%" }}>
            <Typography
              color="shadesOfDark.dark"
              sx={{ mb: 0.5, fontWeight: 700, fontSize: "1.3rem" }}
            >
              Razão social
            </Typography>
            <DropdownSelectAll
              options={options}
              value={companies}
              isSubTitle
              isCNPJ
              label=""
              onChange={(value) => handleMountList(value)}
            />
          </Stack>
        </Stack>

        {/* Botão fixo no rodapé */}
        <Stack
          sx={{
            px: 2.5,
            py: 2,
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            type="submit"
            variant="contained"
            fullWidth
            onClick={() => handleSubmit()}
            sx={{ minHeight: 48, fontSize: "1.3rem" }}
          >
            Filtrar
          </Button>
        </Stack>
      </Drawer>
    </Fragment>
  );
};
